import {
  GovernancePolicyFlags,
  PolicyViolationOutcomeMap,
  violationsCountToGovernanceFlags,
} from '@agilelab/plugin-wb-governance-common';
import { Box } from '@material-ui/core';
import FlagIcon from '@material-ui/icons/Flag';
import clsx from 'clsx';
import { CSSProperties, ReactNode } from 'react';
import { selectOnViolationSeverity } from './utils';
import { flagBoxUseStyles } from './FlagBox';
import { WbInfoTooltip } from '../WbInfoTooltip';

const getTooltip = (
  count: number,
  type: 'info' | 'warning' | 'error',
): string => {
  if (count === 1) return `${count} validation ${type}`;
  if (count > 1) return `${count} validation ${type}s`;
  return '';
};

function generateAriaLabel(flags: GovernancePolicyFlags): string {
  return selectOnViolationSeverity(
    flags,
    {
      error: getTooltip(flags.error, 'error'),
      warning: getTooltip(flags.warning, 'warning'),
      info: getTooltip(flags.info, 'info'),
    },
    '',
  );
}

function generateTooltipAndIcon(
  flags: GovernancePolicyFlags,
  classes: ReturnType<typeof flagBoxUseStyles>,
): { tooltipContent: ReactNode | undefined; iconClass: string | undefined } {
  return selectOnViolationSeverity(
    flags,
    {
      error: {
        tooltipContent: (
          <div style={{ whiteSpace: 'pre-line' }}>
            {getTooltip(flags.error, 'error')} {'\n'}
          </div>
        ),
        iconClass: clsx(classes.iconBase, classes.errorIcon),
      },
      warning: {
        tooltipContent: getTooltip(flags.warning, 'warning'),
        iconClass: clsx(classes.iconBase, classes.warningIcon),
      },
      info: {
        tooltipContent: getTooltip(flags.info, 'info'),
        iconClass: clsx(classes.iconBase, classes.infoIcon),
      },
    },
    { tooltipContent: undefined, iconClass: undefined },
  );
}

export type FlagBoxContentProps = {
  policyViolationsOutcomeMap?: PolicyViolationOutcomeMap;

  iconStyle?: CSSProperties;
};

export const FlagBoxContent = (props: FlagBoxContentProps) => {
  const { policyViolationsOutcomeMap } = props;
  const classes = flagBoxUseStyles();

  const { tooltipContent, iconClass } = generateTooltipAndIcon(
    violationsCountToGovernanceFlags(policyViolationsOutcomeMap ?? new Map()),
    classes,
  );

  const ariaLabel = generateAriaLabel(
    violationsCountToGovernanceFlags(policyViolationsOutcomeMap ?? new Map()),
  );

  const flagIconWithTooltip = tooltipContent && (
    <WbInfoTooltip
      title={tooltipContent}
      icon={
        <FlagIcon
          style={props.iconStyle}
          className={iconClass}
          role="img"
          aria-hidden="false"
          aria-label={ariaLabel}
        />
      }
    />
  );

  return <Box>{flagIconWithTooltip}</Box>;
};
